/*
  Build the country list that powers the phone-number country selector.

  The list is derived once at module load from libphonenumber-js's country
  metadata (the source of truth for every country we can dial). Names come
  from `Intl.DisplayNames`, and flag emoji are produced by mapping the ISO
  3166-1 alpha-2 letters into Unicode regional indicator symbols.
*/

import { getCountries, getCountryCallingCode } from 'libphonenumber-js';



/*
  Each ASCII letter A–Z has a regional indicator counterpart at code point
  U+1F1E6 + (letter - 'A'). Concatenating two regional indicators is what
  every modern OS renders as a flag glyph.
*/
const REGIONAL_INDICATOR_OFFSET = 127397;

function isoToFlagEmoji(iso) {
  return iso
    .toUpperCase()
    .replace(/./g, char => String.fromCodePoint(char.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET));
}



/*
  `Intl.DisplayNames` is available in every browser we target and in Node 14+.
  We deliberately use 'en' rather than navigator.language so the selector
  reads consistently for our currently English-only UI. Switch to the user's
  locale when the app ships translated copy.
*/
const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });



const phoneCountries = getCountries()
  .map(iso => ({
    iso,
    name: displayNames.of(iso) || iso,
    callingCode: getCountryCallingCode(iso),
    flag: isoToFlagEmoji(iso)
  }))
  .sort((a, b) => a.name.localeCompare(b.name));



export default function getPhoneCountries(pinnedIso) {
  const pinnedCountry = phoneCountries.find(country => country.iso === pinnedIso);

  if (!pinnedCountry) return phoneCountries;

  return [pinnedCountry, ...phoneCountries.filter(country => country !== pinnedCountry)];
}

export { isoToFlagEmoji };
